import { useCallback, useContext, useMemo, useRef } from 'react';
import { useHoverDirty } from 'react-use';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useSiteTheme } from 'src/contexts/SiteTheme/SiteThemeContext';
import { vars } from 'src/core/themes';
import { Stack, Tooltip } from 'src/core/ui';
import { PlusTicketIcon } from 'src/svgs/PlusTicketIcon';
import {
  EventIdQueryParam,
  PurchaseDeeplinkQueryParam,
} from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { addDeepLinkUrlPart } from 'src/utils/deepLinkUtils';
import { getPurchaseOrderDetailsModalConfigWithDeepLink } from 'src/utils/purchaseUtils';
import { Event } from 'src/WebApiController';

import * as styles from './EventActions.css';

export const LaunchAddPurchase = ({ event }: { event: Event }) => {
  const { isDarkMode } = useSiteTheme();

  const elementRef = useRef<HTMLDivElement>(null);
  const isHovering = useHoverDirty(elementRef);

  const { setModal } = useContext(ModalContext);

  const addPurchase = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      e.preventDefault();

      // To display addPurchaseModal for a concrete event,
      // the URL must contain ?eventId=<eventId>&uniquePurchaseId=0
      addDeepLinkUrlPart(EventIdQueryParam, event.virtId);
      addDeepLinkUrlPart(PurchaseDeeplinkQueryParam, '0');

      setModal(getPurchaseOrderDetailsModalConfigWithDeepLink());
    },
    [event.virtId, setModal]
  );

  const iconColor = useMemo(() => {
    if (isHovering) {
      return vars.color.iconHover;
    }
    return vars.color.textPrimary;
  }, [isHovering]);

  return (
    <Tooltip
      open={isHovering}
      delayDuration={100}
      contentVariant={isDarkMode ? 'light' : 'dark'}
      align="center"
      triggerContent={
        <Stack
          ref={elementRef}
          onClick={addPurchase}
          className={styles.eventActionsGroupItem}
        >
          <PlusTicketIcon
            withHoverEffect
            fill={iconColor}
            size={vars.iconSize.m}
          />
        </Stack>
      }
    >
      <Content id={ContentId.AddPurchase} />
    </Tooltip>
  );
};
